import React from 'react';
import { useWorkspacePath } from 'pages/workspaces/useWorkspace';
import { basePaths } from 'app-constants';
import { ScrollSyncLink } from 'features/ScrollSync';
import { useQueryAndHash } from './useQueryAndHash';
import { LinkHelperProps } from './index';
import { TrackedLink } from 'components/common/TrackedLink';

interface SecondarySourceLinkProps extends LinkHelperProps {
  practiceAreaSlug?: string;
  productCode?: string;
  onlawId?: string;
}

export function useSecondarySourcePath(
  practiceAreaSlug?: string,
  productCode?: string,
  onlawId?: string,
  query?: string | null,
  hash?: string | null,
) {
  const workspacePath = useWorkspacePath();

  const getPath = useGetSecondarySourcePath();
  const path = React.useMemo(
    () => workspacePath + getPath(practiceAreaSlug, productCode, onlawId),
    [getPath, practiceAreaSlug, productCode, onlawId, workspacePath],
  );

  return useQueryAndHash(path, query, hash);
}

export function useGetSecondarySourcePath() {
  return React.useCallback(
    (practiceAreaSlug?: string, productCode?: string, onlawId?: string) => {
      const parts = [basePaths.secondarySources];

      if (practiceAreaSlug || productCode || onlawId) {
        parts.push('area');
        if (practiceAreaSlug) parts.push(practiceAreaSlug);
        if (productCode) parts.push(productCode);
        if (onlawId) parts.push(onlawId);
      }

      return parts.join('/').replace(/\/+$/, '');
    },
    [],
  );
}

export function SecondarySourceLink({
  to,
  practiceAreaSlug,
  productCode,
  onlawId,
  query,
  hash,
  ...props
}: SecondarySourceLinkProps) {
  const path = useSecondarySourcePath(
    practiceAreaSlug,
    productCode,
    onlawId,
    query,
    hash,
  );
  return <TrackedLink to={to ? to : path} {...props} />;
}
